import type { ComponentProps, ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { cn } from '@repo/lib/cn';
import { HelpCircleIcon, InfoIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import type { CorrectType } from '@/components/ui/quiz/correct-icon';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { NumberInputVertical } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from '@/components/ui/input-group';
import { CorrectIcon, PointIcon } from '@/components/ui/quiz';
import { Tiptap } from '@/components/ui/tiptap-editor';
import { quizExtensionSet } from '@/lib/tiptap-extensions-sets';

interface QuestionMarkedWrapperProps extends ComponentProps<'div'> {
  showMarked: boolean;
}
export function QuestionMarkedWrapper(questionMarkedWrapperProps: QuestionMarkedWrapperProps) {
  const { showMarked, className, ...props } = questionMarkedWrapperProps;

  return (
    <div
      data-slot="question-marked"
      data-hidden={(!showMarked).toString()}
      className={cn(
        'border-border-primary divide-border-primary bg-card h-fit divide-y overflow-hidden rounded-lg border opacity-100 shadow-sm transition-all duration-400 data-[hidden=true]:max-h-0 data-[hidden=true]:opacity-0',
        className
      )}
      {...props}
    />
  );
}

interface QuestionMarkedHeaderProps extends ComponentProps<'div'> {
  score: number;
  totalPoints: number;
  correct: boolean;
  manualCheck?: boolean;
  correctType: CorrectType;
  headerActions?: ReactNode;
}

export function QuestionMarkedHeader(questionMarkedHeaderProps: QuestionMarkedHeaderProps) {
  const { score, totalPoints, className, correctType, headerActions, correct, manualCheck, ...props } = questionMarkedHeaderProps;

  const correctText = useMemo<string | undefined>(() => {
    switch (correctType) {
      case 'waiting':
        return 'Nie sprawdzono jeszcze';
      case 'partially':
        return 'Częściowo poprawny';
      case null:
        return 'Nie odpowiedziano';
      case true:
        return 'Poprawny';
      case false:
        return 'Niepoprawny';
    }
  }, [correctType]);

  return (
    <div data-manual-check={manualCheck?.toString()} data-correct={correct.toString()} className={cn('flex items-center gap-3 p-4', className)} {...props}>
      <div className="space-y-1.5">
        <span className="text-muted-foreground text-xs">Punkty</span>
        <div className="flex items-center gap-1">
          <PointIcon className="size-4" />
          <p className="text-accent-foreground text-sm">
            {score}/{totalPoints}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <span className="text-muted-foreground text-xs">Wynik</span>
        <div className="flex items-center gap-1">
          <CorrectIcon answerState={correctType} size={'sm'} />
          <p className="text-accent-foreground text-sm">{correctText}</p>
        </div>
      </div>
      <div className="ms-auto shrink-0">{headerActions}</div>
    </div>
  );
}

export interface QuestionMarkedQuestionContentProps extends ComponentProps<'div'> {
  showQuestion: boolean;
  content: string;
}

export function QuestionMarkedQuestionContent(questionMarkedQuestionContentProps: QuestionMarkedQuestionContentProps) {
  const { showQuestion, content, className, children, ...props } = questionMarkedQuestionContentProps;
  if (!showQuestion) return null;

  return (
    <div data-slot="question-marked-question-content" className={cn('space-y-4 p-4', className)} {...props}>
      <Tiptap extensions={quizExtensionSet} editable={false} content={content} />
      {children}
    </div>
  );
}

export function QuestionMarkedUserAnswer(questionMarkedUserAnswerProps: ComponentProps<'div'>) {
  const { className, ...props } = questionMarkedUserAnswerProps;
  return <div data-slot="question-marked-user-answer" className={cn('bg-accent/75 space-y-3 p-4', className)} {...props} />;
}

export function QuestionMarkedResponseCard(questionMarkedResponseCardProps: ComponentProps<'div'>) {
  const { className, ...props } = questionMarkedResponseCardProps;
  return <div className={cn('bg-card space-y-3 rounded-lg border p-2 shadow-xs', className)} {...props} />;
}

export function QuestionMarkedResponseHeader(questionMarkedResponseHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = questionMarkedResponseHeaderProps;
  return <div className={cn('flex flex-row justify-between', className)} {...props} />;
}

export function QuestionMarkedResponseContent(questionMarkedResponseContentProps: ComponentProps<'div'>) {
  const { className, ...props } = questionMarkedResponseContentProps;
  return <div className={cn('space-y-1', className)} {...props} />;
}

export interface QuestionMarkedCorrectToggleFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
}

export function QuestionMarkedCorrectToggleField<TFieldValues extends FieldValues>(questionMarkedCorrectToggleFieldProps: QuestionMarkedCorrectToggleFieldProps<TFieldValues>) {
  const { control, name } = questionMarkedCorrectToggleFieldProps;
  return (
    <FormField
      name={name}
      control={control}
      render={({ field: { onChange, value, ...rest } }) => (
        <FormItem>
          <FormControl>
            <Button type="button" variant="outline" className="w-fit px-0 has-data-disabled:cursor-not-allowed has-data-disabled:opacity-60" onClick={() => onChange(!value)} {...rest}>
              <CorrectIcon answerState={!!value} size="default" />
            </Button>
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export interface QuestionMarkedPointsFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  maxPoints?: number;
}

export function QuestionMarkedPointsField<TFieldValues extends FieldValues>(questionMarkedPointsFieldProps: QuestionMarkedPointsFieldProps<TFieldValues>) {
  const { control, name, maxPoints } = questionMarkedPointsFieldProps;

  return (
    <FormField
      name={name}
      control={control}
      render={({ field: pointsField }) => (
        <FormItem className="w-fit">
          <FormLabel className="hidden">
            Przydzielone punkty <small className="text-muted-foreground text-sm">(max {maxPoints} pkt)</small>
          </FormLabel>
          <FormControl>
            <NumberInputVertical {...pointsField} step={0.5} min={0} max={maxPoints} value={pointsField.value} onChange={pointsField.onChange} placeholder={`np. ${maxPoints ?? 1}`} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export interface QuestionMarkedFormActionsProps {
  onClose: () => void;
}

export function QuestionMarkedFormActions(questionMarkedFormActionsProps: QuestionMarkedFormActionsProps) {
  const { onClose } = questionMarkedFormActionsProps;

  return (
    <div className="flex justify-end gap-3 pt-3">
      <Button variant="tertiary" type="button" onClick={onClose}>
        Anuluj
      </Button>
      <Button variant="secondary" type="submit">
        Zapisz
      </Button>
    </div>
  );
}

export interface QuestionMarkedNoAnswerProps extends ComponentProps<'div'> {
  showMarked: boolean;
}

export function QuestionMarkedNoAnswer(questionMarkedNoAnswerProps: QuestionMarkedNoAnswerProps) {
  const { showMarked, className, ...props } = questionMarkedNoAnswerProps;
  return (
    <QuestionMarkedWrapper showMarked={showMarked} className={cn('text-accent-foreground flex items-center gap-4 p-4', className)} {...props}>
      <InfoIcon className="m-0 size-4" />
      <p className="text-sm">Nie odpowiedziano na pytanie</p>
    </QuestionMarkedWrapper>
  );
}

export function QuestionMarkedDescription() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Badge variant={'muted'} size={'sm'}>
        <HelpCircleIcon />
      </Badge>
      <p className="text-muted-foreground text-sm">Uzasadnij ocenę lub skoryguj punkty i status odpowiedzi ucznia, jeśli to konieczne.</p>
    </div>
  );
}

export interface QuestionMarkedExplanationFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  placeholder?: string;
  buttonLabel?: string;
}

export function QuestionMarkedExplanationField<TFieldValues extends FieldValues>(questionMarkedExplanationFieldProps: QuestionMarkedExplanationFieldProps<TFieldValues>) {
  const { control, name, placeholder = 'Wpisz uzasadnienie oceny', buttonLabel = 'Uzasadnienie' } = questionMarkedExplanationFieldProps;
  const value = useWatch({ control, name });
  const isVisible: boolean = value != null;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {!isVisible ? (
            <Button type="button" variant={'outline'} className="border-dashed" onClick={() => field.onChange('')}>
              <PlusIcon />
              {buttonLabel}
            </Button>
          ) : (
            <InputGroup>
              <FormControl>
                <InputGroupTextarea placeholder={placeholder} {...field} value={field.value ?? ''} onChange={(event) => field.onChange(event.target.value)} />
              </FormControl>
              <InputGroupAddon align="block-end">
                <InputGroupButton className="ml-auto" size="sm" variant={'ghost'} onClick={() => field.onChange(null)}>
                  <Trash2Icon />
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          )}
          <FormMessage className="min-h-0 empty:hidden" />
        </FormItem>
      )}
    />
  );
}
